/**
 * ToolCluster — groups a run of consecutive tool calls into one collapsible
 * activity block (docs/plans/2026-07-04_CHAT_UI_REDESIGN.md §2).
 *
 * Collapsed: one summary line — "Ran N tools · 12s" + aggregate diff badge
 * + status icon. Expanded: the individual ToolCallBlocks behind a hairline
 * rail (.chat-rail). While any member tool is running the cluster is open
 * and the header shows the current tool's label as a single shimmering
 * line (never a stack of spinners); when the run completes it auto-folds —
 * unless the user has toggled it manually, which always wins.
 *
 * A run of ONE tool renders the bare ToolCallBlock — no cluster chrome.
 */
import { useState } from 'react'
import { ChevronRight, ChevronDown, Loader2, CheckCircle2, XCircle, Layers } from 'lucide-react'
import { ToolCallBlock, getToolDiffTotals, describeToolCall } from './ToolCallBlock.js'
import { DiffBadge } from './DiffView.js'
import type { MessagePart } from './hooks.js'

type ToolCallPart = Extract<MessagePart, { type: 'tool-call' }>
type ToolResultPart = Extract<MessagePart, { type: 'tool-result' }>

interface Props {
  parts: ToolCallPart[]
  results: Map<string, ToolResultPart>
  wrapLines?: boolean
  forceInline?: boolean
  workspaceId?: string
  onPathClick?: (path: string) => void
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`
}

export function ToolCluster({ parts, results, wrapLines, forceInline, workspaceId, onPathClick }: Props) {
  const anyActive = parts.some(p => p.status === 'running' || p.status === 'pending')
  // null = auto (open while running, folded when done); boolean = user override
  const [userToggled, setUserToggled] = useState<boolean | null>(null)
  const expanded = userToggled ?? anyActive

  const blocks = parts.map(p => {
    const result = results.get(p.callId)
    return (
      <ToolCallBlock
        key={p.id}
        toolName={p.toolName}
        input={p.input}
        output={result?.output}
        error={result?.error}
        status={p.status}
        title={p.title}
        metadata={p.metadata}
        inputSize={p.inputSize}
        wrapLines={wrapLines}
        forceInline={forceInline}
        workspaceId={workspaceId}
        subagentUpdates={p.subagentUpdates}
        onPathClick={onPathClick}
      />
    )
  })

  if (parts.length === 1) return blocks[0]

  const running = parts.find(p => p.status === 'running') || parts.find(p => p.status === 'pending')
  const doneCount = parts.filter(p => p.status === 'completed' || p.status === 'error').length
  const anyError = parts.some(p => p.status === 'error' || !!results.get(p.callId)?.error)
  const totalMs = parts.reduce((sum, p) => {
    const d = p.metadata?.durationMs
    return typeof d === 'number' ? sum + d : sum
  }, 0)
  const diff = parts.reduce(
    (acc, p) => {
      const t = getToolDiffTotals(p.toolName, p.metadata)
      return t ? { additions: acc.additions + t.additions, deletions: acc.deletions + t.deletions } : acc
    },
    { additions: 0, deletions: 0 },
  )

  return (
    <div className="my-1">
      <button
        onClick={() => setUserToggled(!expanded)}
        className="flex items-center gap-2 py-1 text-xs themed-text-muted hover:themed-text transition-colors max-w-full"
        data-testid="tool-cluster-header"
      >
        {expanded ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
        {anyActive ? (
          <>
            <Loader2 size={11} className="animate-spin text-blue-400 shrink-0" />
            <span className="chat-shimmer truncate">
              {running ? describeToolCall(running.toolName, running.input, running.title) : 'Working…'}
            </span>
            {doneCount > 0 && <span className="shrink-0 tabular-nums">· {doneCount} done</span>}
          </>
        ) : (
          <>
            <Layers size={11} className="shrink-0" />
            <span className="shrink-0">Ran {parts.length} tools</span>
            {totalMs > 0 && <span className="shrink-0 tabular-nums">· {formatDuration(totalMs)}</span>}
            {(diff.additions > 0 || diff.deletions > 0) && (
              <DiffBadge additions={diff.additions} deletions={diff.deletions} />
            )}
            {anyError
              ? <XCircle size={12} className="text-red-400 shrink-0" />
              : <CheckCircle2 size={12} className="text-green-400 shrink-0" />}
          </>
        )}
      </button>
      {expanded && <div className="chat-rail space-y-1 py-0.5">{blocks}</div>}
    </div>
  )
}
